import * as React from 'react'
import cn from 'classnames'

// Shared field styling for the New check-in cycle modal, used across every step.

export const fieldLabelClass =
  'mb-1 block text-[15px] font-normal leading-[18px] text-[#5E6580]'

export const baseInputClass =
  'h-[52px] w-full rounded-[4px] border-0 bg-[#EFF2F9] px-4 font-talent text-[16px] font-normal leading-[20px] text-[#1D264A] placeholder:text-[#9aa1ba] focus:outline-none focus:ring-2'

export const inputClass = (hasError: boolean): string =>
  cn(
    baseInputClass,
    hasError ? 'ring-2 ring-[#E42309]' : 'focus:ring-[#2E9E5D]',
  )

export const FieldError = ({ message }: { message: string | null }) =>
  message ? (
    <p className="mt-1.5 text-[13px] text-[#E42309]">{message}</p>
  ) : null

// --- Cycle name validation --------------------------------------------------
// Mirrors CheckinCycle::NAME_MAX_LENGTH / #name_has_letter on the server so the
// wizard blocks before a round-trip. Used for both cycle types (Standard
// Check-in and Performance Review) since they share this modal.
export const NAME_MAX_LENGTH = 255

// A name must contain at least one letter — a value made up solely of
// punctuation/symbols ("!@#$") is not a meaningful label. `\p{L}` is
// Unicode-aware, so accented names pass; digits alone do not.
export const nameHasLetter = (value: string): boolean => /\p{L}/u.test(value)

// Names are compared ignoring case and surrounding whitespace, matching
// CheckinCycle#name_unique_among_active_cycles on the server.
const normalizedName = (value: string): string => value.trim().toLowerCase()

// Inline error for the cycle name field, or null when valid. `submitAttempted`
// gates the "required" message so it only appears after a submit try, while the
// too-long / letterless / duplicate messages surface as soon as they apply.
// `takenNames` holds the org's other ACTIVE cycle names (the archived ones are
// free to be reused), so prefilling from an archived template a second time
// reports the clash here instead of quietly creating a duplicate.
export const cycleNameError = (
  value: string,
  submitAttempted: boolean,
  takenNames: string[] = [],
): string | null => {
  const trimmed = value.trim()
  if (trimmed === '') return submitAttempted ? 'Cycle name is required' : null
  if (trimmed.length > NAME_MAX_LENGTH) {
    return `Cycle name must be ${NAME_MAX_LENGTH} characters or fewer`
  }
  if (!nameHasLetter(trimmed))
    return 'Cycle name must include at least one letter'
  if (
    takenNames.some(
      (taken) => normalizedName(taken) === normalizedName(trimmed),
    )
  ) {
    return 'A cycle with this name already exists'
  }
  return null
}

// --- Shared time / timezone helpers ----------------------------------------
// Used by both the cycle wizard (Start Time / Time Zone) and the notification
// editor (send time / timezone) so the option lists and parsing stay in sync.

export const TIME_OPTIONS = Array.from({ length: 24 }, (_, hour) => {
  const period = hour < 12 ? 'AM' : 'PM'
  const display = hour % 12 === 0 ? 12 : hour % 12
  return `${display} ${period}`
})

// Convert a display label like "9 AM" / "12 PM" into the "HH:00" form the
// `time` column parses cleanly.
export const toTwentyFourHour = (label: string): string => {
  const match = label.match(/^(\d{1,2})\s*(AM|PM)$/i)
  if (!match) return label

  let hour = Number(match[1]) % 12
  if (match[2].toUpperCase() === 'PM') hour += 12

  return `${hour.toString().padStart(2, '0')}:00`
}

// Convert a stored time (ISO datetime or "HH:MM[:SS]") back into the "9 AM" /
// "12 PM" display label the time selects use. Defaults to 9 AM.
export const labelFromStoredTime = (value: string | null): string => {
  const match = value?.match(/(?:T|^)(\d{1,2}):(\d{2})/)
  if (!match) return '9 AM'

  const hour24 = Number(match[1])
  const period = hour24 < 12 ? 'AM' : 'PM'
  const display = hour24 % 12 === 0 ? 12 : hour24 % 12
  return `${display} ${period}`
}

// A broad IANA picker. The DB column stores any IANA name, so admins outside
// North America can pick their own zone rather than being forced onto UTC.
export const TIMEZONE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'UTC', label: 'UTC' },
  // Americas
  { value: 'America/New_York', label: 'Eastern Time — New York (EST/EDT)' },
  { value: 'America/Chicago', label: 'Central Time — Chicago (CST/CDT)' },
  { value: 'America/Denver', label: 'Mountain Time — Denver (MST/MDT)' },
  {
    value: 'America/Los_Angeles',
    label: 'Pacific Time — Los Angeles (PST/PDT)',
  },
  { value: 'America/Anchorage', label: 'Alaska Time — Anchorage (AKST/AKDT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time — Honolulu (HST)' },
  { value: 'America/Toronto', label: 'Toronto (ET)' },
  { value: 'America/Sao_Paulo', label: 'São Paulo (BRT)' },
  { value: 'America/Mexico_City', label: 'Mexico City (CT)' },
  // Europe & Africa
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Dublin', label: 'Dublin (GMT/IST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
  { value: 'Europe/Madrid', label: 'Madrid (CET/CEST)' },
  { value: 'Europe/Athens', label: 'Athens (EET/EEST)' },
  { value: 'Europe/Moscow', label: 'Moscow (MSK)' },
  { value: 'Africa/Lagos', label: 'Lagos (WAT)' },
  { value: 'Africa/Johannesburg', label: 'Johannesburg (SAST)' },
  // Asia & Middle East
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Asia/Kolkata', label: 'India — Kolkata (IST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  // Oceania
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZST/NZDT)' },
]
